import React, { useState, useEffect } from "react";
import { Card, Input, message } from "antd";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import StandardButton from '../components/StandardButton';

const TextCounter = () => {
  const { t } = useTranslation();
  const [text, setText] = useState(t("sample_text_for_counter"));

  const [result, setResult] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const id = searchParams.get("share");
    if (id) {
      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tools/text-counter/share/${id}`)
        .then(res => res.json())
        .then(data => {
          if (data.text && data.result) {
            setText(data.text);
            setResult(data.result);
          }
        });
    }
  }, []);

  useEffect(() => {
    if (!text.trim()) {
      setResult(null);
      return;
    }
    countText(text);
  }, [text]);

  const countText = (text) => {
    const words = text.trim().split(/\s+/);
    const wordCount = words.filter(w => w).length;
    const charCountWithSpaces = text.length;
    const charCountWithoutSpaces = text.replace(/\s/g, "").length;
    const sentenceCount = text.split(/[.!?]+/).filter(s => s.trim()).length;
    const paragraphCount = text.split(/\n{2,}/).filter(p => p.trim()).length;
    const lineCount = text.split(/\r\n|\r|\n/).length;
    const uniqueWords = new Set(words.map(w => w.toLowerCase())).size;
    const longestWord = words.reduce((a, b) => (b.length > a.length ? b : a), "");
    const avgWordLength = wordCount ? (charCountWithoutSpaces / wordCount).toFixed(2) : 0;
    const estimatedReadingTime = Math.ceil(wordCount / 200);

    setResult({
      wordCount,
      charCountWithSpaces,
      charCountWithoutSpaces,
      sentenceCount,
      paragraphCount,
      lineCount,
      uniqueWords,
      longestWord,
      avgWordLength,
      estimatedReadingTime,
    });
  };

  const handleCopy = async () => {
    if (!result) return;

    const copyText = `
${t("word_count")}: ${result.wordCount}
${t("char_count_with_spaces")}: ${result.charCountWithSpaces}
${t("char_count_without_spaces")}: ${result.charCountWithoutSpaces}
${t("sentence_count")}: ${result.sentenceCount}
${t("paragraph_count")}: ${result.paragraphCount}
${t("line_count")}: ${result.lineCount}
${t("unique_words")}: ${result.uniqueWords}
${t("longest_word")}: ${result.longestWord}
${t("average_word_length")}: ${result.avgWordLength} ${t("characters")}
${t("estimated_reading_time")}: ${result.estimatedReadingTime} ${t("minutes")}
    `;

    try {
      await navigator.clipboard.writeText(copyText.trim());
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const handleShare = async () => {
    if (!result) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tools/text-counter/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, result }),
      });
      const data = await response.json();
      if (data.id) {
        const link = `${window.location.origin}/tools/text-counter?share=${data.id}`;
        await navigator.clipboard.writeText(link);
        toast.success(t("share_success"));
      } else {
        toast.error(t("share_failed"));
      }
    } catch {
      toast.error(t("share_failed"));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-0">
      <Header />
      <div className="max-w-5xl mx-auto">
        <Card className="p-6 rounded-xl shadow-lg space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-indigo-700">
              {t('text_counter_title')}
            </h1>
            <p className="text-gray-600 mt-1">{t('text_counter_intro')}</p>
          </div>

          <Input.TextArea
            rows={8}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("text_counter_placeholder")}
          />

          {result && (
            <div className="mt-6 space-y-2">
              <div><strong>{t("word_count")}:</strong> {result.wordCount}</div>
              <div><strong>{t("char_count_with_spaces")}:</strong> {result.charCountWithSpaces}</div>
              <div><strong>{t("char_count_without_spaces")}:</strong> {result.charCountWithoutSpaces}</div>
              <div><strong>{t("sentence_count")}:</strong> {result.sentenceCount}</div>
              <div><strong>{t("paragraph_count")}:</strong> {result.paragraphCount}</div>
              <div><strong>{t("line_count")}:</strong> {result.lineCount}</div>
              <div><strong>{t("unique_words")}:</strong> {result.uniqueWords}</div>
              <div><strong>{t("longest_word")}:</strong> {result.longestWord}</div>
              <div><strong>{t("average_word_length")}:</strong> {result.avgWordLength} {t("characters")}</div>
              <div><strong>{t("estimated_reading_time")}:</strong> {result.estimatedReadingTime} {t("minutes")}</div>

              <div className="flex gap-4 mt-6">
                <StandardButton
                  onClick={handleCopy}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t('copy_button')}
                </StandardButton>
                <StandardButton
                  onClick={handleShare}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t('share_button')}
                </StandardButton>
              </div>
            </div>
          )}
        </Card>
        <Footer />
      </div>
    </div>
  );
};

export default TextCounter;